// 2026-07-21 - FormuLearning - 배치 액션 이력 패널 (이슈 #43)
// 2026-08-03 수정 - 이슈 #50: 프로젝트 전체 이력 모드 추가. 배치 단위 조회는 batch_id를
//                   키로 쓰므로 삭제된 배치(batch_id=null)의 이력에 도달할 수 없다.
// 파일 위치: frontend/src/features/step4/BatchHistoryPanel.tsx
// 목적: BR-S4-L17 — 업로드 권한 판정을 폐기한 자리를 액션 이력이 대신한다.
//       무엇을 언제 덮어썼는지·왜 제외했는지를 되짚는다.

import { useEffect, useState } from 'react'
import {
  getBatchActions, getProjectActions, ACTION_LABELS, EXCLUDE_TYPE_LABELS, type BatchAction,
} from '../../api/lot'

interface Props {
  projectId: string
  /** 배치 단위 모드. 생략하면 프로젝트 전체(삭제된 배치 포함) 모드. */
  batchId?: string
  batchCode?: string
  onClose: () => void
}

export default function BatchHistoryPanel({ projectId, batchId, batchCode, onClose }: Props) {
  const [actions, setActions] = useState<BatchAction[] | null>(null)
  const projectMode = !batchId

  useEffect(() => {
    const load = batchId ? getBatchActions(projectId, batchId) : getProjectActions(projectId)
    load.then(setActions).catch(() => setActions([]))
  }, [projectId, batchId])

  // 현존 배치가 아니면(= 삭제분) batch_id가 비어 있다. 프로젝트 모드에서만 나타난다.
  const deletedCount = (actions ?? []).filter((a) => !a.batch_id).length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className={`max-h-[70vh] overflow-y-auto rounded p-4 ${projectMode ? 'w-[36rem]' : 'w-[28rem]'}`}
        style={{ backgroundColor: 'var(--color-bg-panel)', border: '1px solid var(--border-soft)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            {projectMode ? 'Project History — 전체 배치' : `Batch History — ${batchCode}`}
          </h4>
          <button onClick={onClose} className="text-[11px] text-text-secondary hover:text-text-body">
            ✕
          </button>
        </div>

        {projectMode && deletedCount > 0 && (
          <p className="mb-2 text-[10px] text-text-muted">
            삭제된 배치의 이력 <span className="text-warn">{deletedCount}건</span>을 포함합니다 —
            배치는 사라져도 무엇을 했는지는 남습니다.
          </p>
        )}

        {actions === null && <p className="text-[11px] text-text-secondary">Loading...</p>}
        {actions !== null && actions.length === 0 && (
          <p className="text-[11px] text-text-secondary">아직 기록된 액션이 없습니다.</p>
        )}

        {actions !== null && actions.length > 0 && (
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-input)' }}>
                <th className="px-2 py-1 text-left font-medium text-text-secondary">일시</th>
                {projectMode && (
                  <th className="px-2 py-1 text-left font-medium text-text-secondary">배치</th>
                )}
                <th className="px-2 py-1 text-left font-medium text-text-secondary">액션</th>
                <th className="px-2 py-1 text-left font-medium text-text-secondary">내용</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="whitespace-nowrap px-2 py-1 text-text-secondary">
                    {new Date(a.created_at).toLocaleString('ko-KR', {
                      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  {projectMode && (
                    <td className="whitespace-nowrap px-2 py-1 text-text-body">
                      {a.batch_code}
                      {!a.batch_id && (
                        <span className="ml-1 rounded px-1 text-[10px] text-text-muted"
                              style={{ border: '1px solid var(--border-subtle)' }}>
                          삭제됨
                        </span>
                      )}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-2 py-1 text-text-body">
                    {ACTION_LABELS[a.action] ?? a.action}
                  </td>
                  <td className="px-2 py-1 text-text-secondary">
                    {a.exclude_type && (
                      <span className="mr-1 rounded px-1 text-[10px] text-warn"
                            style={{ border: '1px solid var(--border-subtle)' }}>
                        {EXCLUDE_TYPE_LABELS[a.exclude_type]}
                      </span>
                    )}
                    {a.reason || (typeof a.meta?.filename === 'string' ? a.meta.filename : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
